// Shared helpers for rendering/filtering dates in a practice's own configured timezone
// (GeneralCallSettings.SelectedTimezone, resolved server-side and returned alongside
// date-filtered responses as `practiceTimezone` / the `X-Practice-Timezone` header) instead of
// the viewer's browser-local zone. Filtering already happens in that zone server-side (see
// getPracticeTimezone on the backend) - rendering timestamps in the same zone here is what
// makes "what day a call falls on" agree between the filter and what's displayed, instead of
// two independent, uncoordinated conventions. Uses Intl.DateTimeFormat (no timezone library
// dependency exists in this repo - same approach the backend's scheduleTimeUtils.js already
// uses for the outbound dialer scheduler).
export const DEFAULT_PRACTICE_TIMEZONE = "America/New_York";

const toDate = (value) => {
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getPartsInTimezone = (date, timeZone) => {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const parts = {};
  for (const part of formatter.formatToParts(date)) {
    parts[part.type] = part.value;
  }
  return parts;
};

// formatInPracticeTimezone: "YYYY-MM-DD HH:mm:ss" in the given zone - same shape as the
// moment(value).format("YYYY-MM-DD HH:mm:ss") calls this replaces.
export const formatInPracticeTimezone = (value, timeZone = DEFAULT_PRACTICE_TIMEZONE) => {
  const date = toDate(value);
  if (!date) return "";
  const parts = getPartsInTimezone(date, timeZone);
  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`;
};

// formatShortInPracticeTimezone: "MM/DD HH:mm:ss" - matches CallsBreakdownDialog's existing
// moment(...).format("MM/DD HH:mm:ss") shape.
export const formatShortInPracticeTimezone = (value, timeZone = DEFAULT_PRACTICE_TIMEZONE) => {
  const date = toDate(value);
  if (!date) return "";
  const parts = getPartsInTimezone(date, timeZone);
  return `${parts.month}/${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`;
};

// formatUsDateInPracticeTimezone: "MM/DD/YYYY HH:mm:ss" - matches the Summary tab's existing
// moment(...).format("MM/DD/YYYY HH:mm:ss") shape.
export const formatUsDateInPracticeTimezone = (value, timeZone = DEFAULT_PRACTICE_TIMEZONE) => {
  const date = toDate(value);
  if (!date) return "";
  const parts = getPartsInTimezone(date, timeZone);
  return `${parts.month}/${parts.day}/${parts.year} ${parts.hour}:${parts.minute}:${parts.second}`;
};

// practiceDateKey: the calendar-date part ("YYYY-MM-DD") of `value` as observed in `timeZone` -
// en-CA's default date format is already YYYY-MM-DD, so no manual part-assembly is needed.
export const practiceDateKey = (value, timeZone = DEFAULT_PRACTICE_TIMEZONE) => {
  const date = toDate(value);
  if (!date) return null;
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
};

// todayInPracticeTimezone: "today" as YYYY-MM-DD observed in `timeZone`, instead of the
// browser's local date or a UTC slice - both of which can disagree with the practice's own zone.
export const todayInPracticeTimezone = (timeZone = DEFAULT_PRACTICE_TIMEZONE) =>
  practiceDateKey(new Date(), timeZone);

// wallTimeToUtcDate: the real UTC instant a "YYYY-MM-DDTHH:mm:ss[.sss]" wall-clock time
// corresponds to when observed in `timeZone`. Standard two-pass trick since this repo has no
// timezone library: first treat the wall-clock string as if it were UTC (a "guess" instant),
// then read what that guess actually looks like when formatted in `timeZone` - the difference
// between the guess and that reading is exactly the zone's UTC offset at that instant, which
// corrects the guess into the true instant.
const wallTimeToUtcDate = (wallClockIso, timeZone) => {
  const utcGuess = new Date(`${wallClockIso}Z`);
  if (Number.isNaN(utcGuess.getTime())) return null;

  const parts = getPartsInTimezone(utcGuess, timeZone);
  const guessedLocalAsUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second),
  );
  const offsetMs = utcGuess.getTime() - guessedLocalAsUtc;

  return new Date(utcGuess.getTime() + offsetMs);
};

// startOfDayInPracticeTimezone / endOfDayInPracticeTimezone: the real UTC instant for a
// "YYYY-MM-DD" date key's midnight (start) or 23:59:59.999 (end) AS OBSERVED in `timeZone`. Use
// these instead of `new Date(dateKey)` (always UTC midnight, regardless of timeZone) whenever a
// plain date-only string needs to become a filter boundary compared against timestamps that are
// otherwise displayed/reasoned about in the practice's own timezone - otherwise a UTC-behind
// practice can end up excluding most or all of its selected end day, and a UTC-ahead practice can
// end up including calls from the day before its selected start day.
export const startOfDayInPracticeTimezone = (dateKey, timeZone = DEFAULT_PRACTICE_TIMEZONE) =>
  dateKey ? wallTimeToUtcDate(`${dateKey}T00:00:00`, timeZone) : null;

export const endOfDayInPracticeTimezone = (dateKey, timeZone = DEFAULT_PRACTICE_TIMEZONE) =>
  dateKey ? wallTimeToUtcDate(`${dateKey}T23:59:59.999`, timeZone) : null;
